/**
 * Запись партии на сервер ПО ХОДУ игры: прохождение главы обучения и
 * локальный матч (у них разный транспорт и разный повод для отправки, а
 * механика записи общая — отсюда «тьюториальное» имя файла у общего движка).
 *
 * Сетевой матч пишется одним документом в конце партии. Обучение так писать
 * нельзя: смысл записи — увидеть, где игрок бросил главу, а брошенная глава
 * «конца» не имеет (закрыл вкладку, ушел в меню, пропала сеть). Поэтому
 * экшены уходят на сервер сразу, как применились: мелкими пачками (все, что
 * накопилось за FLUSH_DELAY_MS — каскад завершения хода диспатчит десятки
 * экшенов синхронно), по одному запросу за раз.
 *
 * Прохождение. Идентификатор прохождения (runId) придумывает клиент: создание
 * записи на сервере можно повторять сколько угодно — запись та же, а
 * завершение главы найдет ее, даже если ответ на создание так и не пришел.
 *
 * Порядок и целостность. Каждая пачка несет индекс, с которого продолжает
 * запись (fromIndex). Сервер дописывает только то, чего у него еще нет:
 * повтор пачки после обрыва связи (запрос дошел, ответ нет) не задваивает
 * запись, а пропуск (у сервера меньше, чем мы считали подтвержденным) он
 * возвращает 409 со своим счетчиком.
 *
 * Порядок внутри диспатча. Экшен, пройдя этот слой, может вызвать вложенные
 * диспатчи (модалки, продвижение сценария) — они применяются раньше, чем
 * middleware узнает, изменил ли внешний экшен состояние. Поэтому место в
 * записи резервируется ДО применения (reserveSlot) и заполняется после
 * (fillSlot): запись идет в порядке применения к редьюсерам.
 *
 * Запись — вспомогательный инструмент: никакая ее ошибка не должна мешать
 * самому обучению. Сервер недоступен — копим и повторяем с растущей паузой;
 * отказ (400/401/403/404, запись переполнена) — запись этой главы
 * прекращается молча.
 */

export const TUTORIAL_REPLAY_FLUSH_DELAY_MS = 300;
// Повод отправить накопленное: по таймеру (обучение — запись должна успеть
// уйти, даже если игрок бросит главу на первом шаге) или вручную, по вызову
// flushNow (локальный матч — раз в раунд, чтобы не бомбить сервер запросами)
export const RUN_RECORDER_FLUSH_MODES = {TIMER: 'timer', MANUAL: 'manual'};
// Пачка укладывается в лимит тела запроса сервера (express.json — 100 КБ) и в
// лимит keepalive-запросов браузера (64 КБ на все такие запросы разом)
export const TUTORIAL_REPLAY_MAX_BATCH_BYTES = 56 * 1024;
export const TUTORIAL_REPLAY_MAX_BATCH_ACTIONS = 200;
// Сервер недоступен так долго, что очередь разрослась, — запись этой главы
// бросаем, а не копим память до бесконечности
export const TUTORIAL_REPLAY_MAX_QUEUE = 5000;
const RETRY_BASE_DELAY_MS = 1000;
const RETRY_MAX_DELAY_MS = 30000;
const CREATE_RUN_MAX_ATTEMPTS = 10;

const STOP_STATUSES = new Set([400, 401, 403, 404]);

const textEncoder = typeof TextEncoder !== 'undefined' ? new TextEncoder() : null;

// Размер в байтах, а не в символах: лимиты тела запроса и keepalive байтовые,
// а кириллица (имена игроков) занимает по два байта на символ
const getRecordBytes = (record) => {
  try {
    const json = JSON.stringify(record);
    return textEncoder ? textEncoder.encode(json).length : json.length * 2;
  } catch (error) {
    return Infinity;
  }
};

// Идентификатор прохождения — UUID: сервер другой не примет (см.
// checkIfValidTutorialRunId)
export const createDefaultRunId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Запасной вариант без crypto.randomUUID (старые браузеры): тот же формат
  const hex = () => Math.floor(Math.random() * 16).toString(16);
  const part = (length) => Array.from({length}, hex).join('');
  return `${part(8)}-${part(4)}-4${part(3)}-a${part(3)}-${part(12)}`;
};

/**
 * @param {Object} deps
 * @param {Object} deps.transport - {createRun(chapterId, runId, actions), appendActions(matchId, body, {keepalive})},
 *   оба возвращают Promise<{status, data}> (сетевой сбой — reject)
 * @param {Function} [deps.now]
 * @param {Function} [deps.setTimer]
 * @param {Function} [deps.clearTimer]
 * @param {Function} [deps.subscribeToPageHide] - (onHide) => unsubscribe
 * @param {Function} [deps.createRunId]
 */
export const createTutorialReplayRecorder = ({
  transport,
  now = () => Date.now(),
  setTimer = (callback, delayMs) => setTimeout(callback, delayMs),
  clearTimer = (timerId) => clearTimeout(timerId),
  subscribeToPageHide = null,
  createRunId = createDefaultRunId,
  flushMode = RUN_RECORDER_FLUSH_MODES.TIMER,
  // Страховка ручного режима: партия с длинным раундом не должна копить
  // неотправленное бесконечно (вкладку закроют — потеряется хвост)
  maxPendingActions = TUTORIAL_REPLAY_MAX_BATCH_ACTIONS * 2,
  maxPendingBytes = TUTORIAL_REPLAY_MAX_BATCH_BYTES,
}) => {
  let session = null;
  let sessionSeq = 0;

  const createSession = (runContext, {recordActions = true} = {}) => {
    sessionSeq += 1;
    let resolveRunCreated;
    const runCreatedPromise = new Promise((resolve) => {
      resolveRunCreated = resolve;
    });
    return {
      id: sessionSeq,
      runContext,
      // Пишутся ли сами действия. false — запись сводится к факту партии
      // (партия из одних ботов, см. checkIfHumanPlayerPresent): документ
      // матча на сервере заводится и закрывается итогами, а действий в нем нет
      recordActions,
      runId: createRunId(),
      matchId: null,
      isStopped: false,
      isEnded: false,
      // Зарезервированные места в порядке применения экшенов (заполняются
      // после применения) и готовые к отправке записи
      slots: [],
      queue: [],
      // Сколько записей сервер подтвердил: queue[0] — запись с этим индексом
      ackCount: 0,
      inFlight: null,
      flushTimerId: null,
      retryTimerId: null,
      retryDelayMs: RETRY_BASE_DELAY_MS,
      // Пачка не пролезла в лимит тела запроса — следующая идет по одной записи
      isSingleRecordBatch: false,
      progress: null,
      runCreatedPromise,
      resolveRunCreated,
      unsubscribePageHide: null,
    };
  };

  const clearTimers = (current) => {
    if (current.flushTimerId !== null) {
      clearTimer(current.flushTimerId);
      current.flushTimerId = null;
    }
    if (current.retryTimerId !== null) {
      clearTimer(current.retryTimerId);
      current.retryTimerId = null;
    }
  };

  const unsubscribePageHide = (current) => {
    current.unsubscribePageHide?.();
    current.unsubscribePageHide = null;
  };

  const stopSession = (current) => {
    current.isStopped = true;
    current.queue = [];
    current.slots = [];
    clearTimers(current);
    current.resolveRunCreated(current.matchId);
    unsubscribePageHide(current);
  };

  const detachIfDone = (current) => {
    // Глава закончилась и все отправлено — сессия больше не нужна
    if (current.isEnded && !current.inFlight && current.queue.length === 0) {
      clearTimers(current);
      unsubscribePageHide(current);
    }
  };

  const takeBatch = (queue, {maxActions = TUTORIAL_REPLAY_MAX_BATCH_ACTIONS} = {}) => {
    const batch = [];
    let bytes = 0;
    for (const record of queue) {
      const recordBytes = getRecordBytes(record);
      if (batch.length > 0
        && (batch.length >= maxActions || bytes + recordBytes > TUTORIAL_REPLAY_MAX_BATCH_BYTES)) {
        break;
      }
      batch.push(record);
      bytes += recordBytes;
    }
    return {batch, bytes};
  };

  const acknowledge = (current, serverCount) => {
    if (!Number.isInteger(serverCount) || serverCount <= current.ackCount) {
      return;
    }
    const confirmed = Math.min(serverCount - current.ackCount, current.queue.length);
    current.queue.splice(0, confirmed);
    current.ackCount = serverCount;
  };

  const scheduleRetry = (current, {retryAfterSeconds} = {}) => {
    if (current.retryTimerId !== null || current.isStopped) {
      return;
    }
    const delayMs = Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
      ? Math.min(retryAfterSeconds * 1000, RETRY_MAX_DELAY_MS)
      : current.retryDelayMs;
    current.retryDelayMs = Math.min(current.retryDelayMs * 2, RETRY_MAX_DELAY_MS);
    current.retryTimerId = setTimer(() => {
      current.retryTimerId = null;
      // eslint-disable-next-line no-use-before-define
      flushSession(current, {keepalive: current.isEnded});
    }, delayMs);
  };

  /**
   * Что делать после ответа на пачку: 'next' — продолжать отправку,
   * 'retry' — повторить позже, 'stop' — прекратить запись главы
   */
  const handleAppendResponse = (current, request, {status, data}) => {
    if (status >= 200 && status < 300) {
      acknowledge(current, Number.isInteger(data?.actionsCount)
        ? data.actionsCount
        : request.fromIndex + request.batchLength);
      current.retryDelayMs = RETRY_BASE_DELAY_MS;
      current.isSingleRecordBatch = false;
      return 'next';
    }
    if (status === 409) {
      // Пропуск на сервере: у него меньше действий, чем мы считали
      // подтвержденными. Продолжаем с его места — пропавшее уже не вернуть
      const serverCount = data?.actionsCount;
      if (!Number.isInteger(serverCount)) {
        return 'retry';
      }
      if (serverCount > current.ackCount) {
        acknowledge(current, serverCount);
      } else {
        current.ackCount = serverCount;
      }
      return 'next';
    }
    if (status === 413) {
      if (data?.isRecordingFull) {
        // Запись главы достигла предела объема — дальше не пишем
        return 'stop';
      }
      if (request.batchLength === 1) {
        // Одиночное действие не влезает в запрос — вместо него метка, чтобы
        // запись не встала навсегда
        const dropped = current.queue[0];
        current.queue[0] = {
          type: 'TUTORIAL_REPLAY_RECORD_DROPPED',
          payload: {actionType: dropped?.type ?? null},
          noServerSync: true,
          recordedAt: dropped?.recordedAt ?? now(),
        };
        return 'next';
      }
      current.isSingleRecordBatch = true;
      return 'next';
    }
    if (STOP_STATUSES.has(status)) {
      return 'stop';
    }
    // 429 (слишком часто), 5xx и прочее — повторить позже
    return 'retry';
  };

  function flushSession(current, {keepalive = false} = {}) {
    if (current.isStopped || !current.matchId || current.queue.length === 0) {
      return;
    }
    if (current.flushTimerId !== null) {
      clearTimer(current.flushTimerId);
      current.flushTimerId = null;
    }

    if (current.inFlight) {
      // Страница закрывается, а обычный запрос еще не вернулся: шлем сразу
      // все неподтвержденное, начиная с летящей пачки. Сервер дописывает
      // только то, чего у него еще нет, — кто бы из двух запросов ни дошел
      // первым, запись не задвоится и хвост не потеряется
      if (keepalive && !current.inFlight.isKeepaliveSent) {
        current.inFlight.isKeepaliveSent = true;
        const {batch} = takeBatch(current.queue);
        transport.appendActions(current.matchId, {
          fromIndex: current.ackCount,
          actions: batch,
          progress: current.progress,
        }, {keepalive: true})
          .then((response) => {
            if (!current.isStopped && response.status >= 200 && response.status < 300) {
              acknowledge(current, response.data?.actionsCount);
            }
          })
          .catch(() => {});
      }
      return;
    }

    const {batch, bytes} = takeBatch(current.queue, {
      maxActions: current.isSingleRecordBatch ? 1 : TUTORIAL_REPLAY_MAX_BATCH_ACTIONS,
    });
    const request = {fromIndex: current.ackCount, batchLength: batch.length, isKeepaliveSent: false};
    current.inFlight = request;

    transport.appendActions(current.matchId, {
      fromIndex: request.fromIndex,
      actions: batch,
      progress: current.progress,
    }, {
      // keepalive-запросы ограничены по размеру — большую пачку шлем обычным
      keepalive: keepalive && bytes <= TUTORIAL_REPLAY_MAX_BATCH_BYTES,
    })
      .then((response) => {
        current.inFlight = null;
        if (current.isStopped) {
          return;
        }
        const outcome = handleAppendResponse(current, request, response);
        if (outcome === 'stop') {
          stopSession(current);
          return;
        }
        if (outcome === 'retry') {
          scheduleRetry(current, {retryAfterSeconds: response.data?.retryAfterSeconds});
          return;
        }
        if (current.queue.length > 0) {
          flushSession(current, {keepalive: current.isEnded});
        } else {
          detachIfDone(current);
        }
      })
      .catch(() => {
        current.inFlight = null;
        scheduleRetry(current);
      });
  }

  // Сколько всего неотправленного накопилось (очередь + зарезервированные,
  // но еще не заполненные места) — для порогов ручного режима
  const getPendingBytes = (current) => current.queue.reduce(
    (total, record) => total + getRecordBytes(record), 0
  );

  const scheduleFlush = (current) => {
    if (current.flushTimerId !== null || current.inFlight || current.retryTimerId !== null) {
      return;
    }
    if (flushMode === RUN_RECORDER_FLUSH_MODES.MANUAL) {
      // Ждем явного flushNow (конец раунда), но не даем очереди разрастись
      if (current.queue.length >= maxPendingActions || getPendingBytes(current) >= maxPendingBytes) {
        flushSession(current);
      }
      return;
    }
    current.flushTimerId = setTimer(() => {
      current.flushTimerId = null;
      flushSession(current);
    }, TUTORIAL_REPLAY_FLUSH_DELAY_MS);
  };

  // Готовые места из начала резерва — в очередь отправки
  const drainSlots = (current) => {
    let moved = false;
    while (current.slots.length > 0 && current.slots[0].isFilled) {
      const {record} = current.slots.shift();
      if (record) {
        current.queue.push(record);
        moved = true;
      }
    }
    if (current.queue.length > TUTORIAL_REPLAY_MAX_QUEUE) {
      console.error('[TutorialReplay] очередь записи переполнена — запись главы прекращена');
      stopSession(current);
      return;
    }
    if (moved) {
      scheduleFlush(current);
    }
  };

  // Запись создана на сервере (ответ на обычное создание или на срочное при
  // закрытии вкладки — какой бы ни пришел первым)
  const handleRunCreated = (current, data) => {
    if (!current.matchId) {
      current.matchId = data.matchId;
      current.resolveRunCreated(current.matchId);
    }
    acknowledge(current, data.actionsCount);
    flushSession(current, {keepalive: current.isEnded});
  };

  const createRun = (current, attempt = 1) => {
    // Запись создается сразу с началом главы (поле, игроки, старт сценария):
    // запись без единого действия нечем было бы проиграть, а вкладку часто
    // закрывают прямо на загрузке
    const {batch: initialActions} = takeBatch(current.queue);
    transport.createRun(current.runContext, current.runId, initialActions)
      .then(({status, data}) => {
        if (current.isStopped) {
          return;
        }
        if (status >= 200 && status < 300 && data?.matchId) {
          handleRunCreated(current, data);
          return;
        }
        if (STOP_STATUSES.has(status) || status === 413 || attempt >= CREATE_RUN_MAX_ATTEMPTS) {
          stopSession(current);
          return;
        }
        throw new Error(`HTTP ${status}`);
      })
      .catch(() => {
        if (current.isStopped) {
          return;
        }
        if (attempt >= CREATE_RUN_MAX_ATTEMPTS) {
          stopSession(current);
          return;
        }
        const delayMs = Math.min(RETRY_BASE_DELAY_MS * (2 ** (attempt - 1)), RETRY_MAX_DELAY_MS);
        setTimer(() => {
          if (!current.isStopped) {
            createRun(current, attempt + 1);
          }
        }, delayMs);
      });
  };

  /**
   * Отправить накопленное немедленно: вкладку закрывают, сворачивают или
   * глава закончилась. Если запись на сервере еще не создана (создание в пути
   * или ждет повтора после сбоя), создание уходит keepalive-запросом сразу со
   * всем накопленным: оно идемпотентно по runId, а таймер повтора умрет вместе
   * со страницей
   */
  const sendUrgently = (current) => {
    if (current.isStopped) {
      return;
    }
    if (current.matchId) {
      flushSession(current, {keepalive: true});
      return;
    }
    const {batch, bytes} = takeBatch(current.queue);
    transport.createRun(current.runContext, current.runId, batch, {
      keepalive: bytes <= TUTORIAL_REPLAY_MAX_BATCH_BYTES,
    })
      .then(({status, data}) => {
        if (!current.isStopped && status >= 200 && status < 300 && data?.matchId) {
          handleRunCreated(current, data);
        }
      })
      .catch(() => {});
  };

  const isActive = () => Boolean(session && !session.isStopped && !session.isEnded);

  return {
    /**
     * Старт записи главы: запись в истории матчей создается на сервере сразу,
     * экшены копятся с этого момента и уходят, как только запись создана.
     *
     * recordActions: false — записать только факт партии, без ее действий
     * (см. одноименное поле сессии)
     */
    beginRun(runContext, {recordActions = true} = {}) {
      if (isActive()) {
        this.endRun();
      }
      const current = createSession(runContext, {recordActions});
      session = current;
      if (subscribeToPageHide) {
        // Вкладку закрывают или сворачивают — отправляем накопленное сразу,
        // не дожидаясь таймера: следующего шанса может не быть
        current.unsubscribePageHide = subscribeToPageHide(() => sendUrgently(current));
      }
      // Создание — после синхронного старта главы: бутстрап (поле, игроки,
      // старт сценария) диспатчится сразу за beginRun и уходит в том же
      // запросе
      setTimer(() => {
        if (!current.isStopped) {
          createRun(current);
        }
      }, 0);
      return current.runId;
    },

    isRecording: isActive,

    getMatchId() {
      return session?.matchId ?? null;
    },

    /**
     * Забыть прохождение совсем: следующая партия этой вкладки не должна
     * закрывать чужую запись. Нужно там, где партия НЕ пишется (сюжетная
     * миссия, редактор миссий, режим зрителя): getRunInfo отдает прохождение и
     * после конца записи, и без этого итоги такой партии уехали бы с runId
     * предыдущей — сервер закрыл бы ее матч, а свой не завел
     */
    forgetRun() {
      this.endRun();
      session = null;
    },

    /**
     * Прохождение текущей главы — для сохранения ее завершения: глава и
     * идентификатор прохождения. Отдается и после остановки записи: запись о
     * главе к этому моменту может уже быть на сервере (переполнилась, сбой
     * после создания), и завершение должно попасть в нее, а не породить
     * вторую. Сервер сам проверяет, есть ли такая запись у игрока
     */
    getRunInfo() {
      if (!session) {
        return null;
      }
      return {
        // Обучение отдает главу строкой — для него контекст и есть chapterId;
        // у локального матча контекст не строка и завершению не нужен
        chapterId: typeof session.runContext === 'string' ? session.runContext : null,
        runId: session.runId,
        matchId: session.matchId,
      };
    },

    /**
     * Отправить накопленное сейчас (ручной режим: закончился раунд локальной
     * партии, подведены итоги). Обычный запрос, не keepalive
     */
    flushNow() {
      if (!isActive()) {
        return;
      }
      flushSession(session);
    },

    reserveSlot() {
      // Действия не пишутся — место резервировать не для чего; отсюда же
      // пустыми остаются appendRecord (он идет через reserveSlot) и отправка
      if (!isActive() || !session.recordActions) {
        return null;
      }
      const slot = {sessionId: session.id, isFilled: false, record: null};
      session.slots.push(slot);
      return slot;
    },

    fillSlot(slot, record) {
      if (!slot || !session || slot.sessionId !== session.id || session.isStopped) {
        return;
      }
      slot.isFilled = true;
      slot.record = record || null;
      drainSlots(session);
    },

    // Запись вне потока применения (метки): встает в текущее место очереди
    appendRecord(record) {
      if (!isActive() || !record) {
        return;
      }
      const slot = this.reserveSlot();
      this.fillSlot(slot, record);
    },

    setProgress(progress) {
      // Прогресс уезжает на сервер вместе с пачкой действий — без действий
      // его некуда положить
      if (session && !session.isStopped && session.recordActions) {
        session.progress = progress || null;
      }
    },

    /**
     * Конец записи главы (выход в меню, следующая глава, размонтирование
     * партии): последняя метка и немедленная отправка остатка
     */
    endRun(finalRecord = null) {
      const current = session;
      if (!current || current.isEnded || current.isStopped) {
        return;
      }
      if (finalRecord) {
        this.appendRecord(finalRecord);
      }
      current.isEnded = true;
      // Незаполненные места (выход посреди диспатча) уже не заполнятся
      current.slots = current.slots.filter((slot) => slot.isFilled);
      drainSlots(current);
      sendUrgently(current);
      detachIfDone(current);
    },

    // Для тестов и диагностики
    getDebugState() {
      if (!session) {
        return null;
      }
      return {
        runContext: session.runContext,
        runId: session.runId,
        matchId: session.matchId,
        ackCount: session.ackCount,
        queueLength: session.queue.length,
        isStopped: session.isStopped,
        isEnded: session.isEnded,
        isInFlight: Boolean(session.inFlight),
      };
    },
  };
};
